import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import CtaBanner from "../components/CtaBanner";
import Reviews from "../components/Reviews";
import Gallery from "../components/Gallery";
import Team from "../components/Team";
import { BUSINESS, BEAUTIX_IG } from "../data/business";
import { Sparkles, Heart, ShieldCheck, GraduationCap } from "lucide-react";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Czystość i higiena",
    desc: "Autoklaw, jednorazowe frezy w podologii i pełna dezynfekcja po każdej klientce.",
  },
  {
    icon: GraduationCap,
    title: "Wiedza i szkolenia",
    desc: "Regularne kursy w Polsce i za granicą — nadążamy za każdą nową techniką.",
  },
  {
    icon: Heart,
    title: "Indywidualne podejście",
    desc: "Zaczynamy od pytania: czego dziś potrzebujesz? Reszta wynika z odpowiedzi.",
  },
  {
    icon: Sparkles,
    title: "Widoczne efekty",
    desc: "Nie sprzedajemy magii. Efekt każdego zabiegu można zmierzyć i zobaczyć.",
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="O BEAUTIX — Studio Kosmetyki Nowoczesnej Katowice Ligota"
        description="Poznaj Studio Kosmetyki Nowoczesnej BEAUTIX w Katowicach. 25 lat doświadczenia, doświadczone stylistki paznokci, podolog i kosmetolog. Laureat Złota Firma 2022 – 2026."
        keywords="salon kosmetyczny Katowice, salon urody Katowice Ligota, studio kosmetyki Katowice, BEAUTIX o nas"
        path="/o-nas"
      />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-4">
        <Breadcrumbs items={[{ label: "O nas" }]} />
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <p className="eyebrow mb-5">Studio Kosmetyki Nowoczesnej</p>
          <h1 className="font-serif-display text-5xl md:text-6xl lg:text-[5rem] leading-[1.02] tracking-tight">
            Salon <span className="italic text-[var(--beautix-pink)]">BEAUTIX</span> —
            <br />
            miejsce dla Ciebie
            <br />
            w Katowicach.
          </h1>
          <p className="mt-8 text-lg text-[var(--beautix-muted)] leading-relaxed font-light">
            BEAUTIX to nowoczesny salon kosmetyczny w katowickiej Ligocie, w którym
            od 25 lat łączymy stylizację paznokci, podologię i kosmetykę twarzy.
            Zaczęłyśmy jako mały gabinet paznokciowy — dziś zespół BEAUTIX to
            trzy doświadczone stylistki, kosmetolog i podolog.
          </p>
        </div>
        <div className="lg:col-span-6 aspect-[4/5] overflow-hidden rounded-tl-[80px]">
          <img
            src="/assets/ig/about-work.jpg"
            alt="Stylistka BEAUTIX podczas pracy nad pedicure hybrydowym — Katowice Ligota"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      <section className="bg-[var(--beautix-cream)] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">Nasze wartości</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">
              To, na czym nam
              <br />
              <span className="italic text-[var(--beautix-pink)]">naprawdę</span> zależy.
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {VALUES.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="p-8 bg-white border border-[var(--beautix-line)] flex flex-col gap-3"
                data-testid={`value-${title}`}
              >
                <div className="w-11 h-11 rounded-full bg-[var(--beautix-blush)] flex items-center justify-center text-[var(--beautix-pink)]">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif-display text-2xl">{title}</h3>
                <p className="text-sm text-[var(--beautix-muted)] leading-relaxed font-light">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-24">
        <p className="eyebrow mb-4 text-center">Nasza historia</p>
        <h2 className="font-serif-display text-4xl md:text-5xl text-center leading-[1.05] mb-12">
          Od pasji do miejsca, do którego{" "}
          <span className="italic text-[var(--beautix-pink)]">chce się wracać</span>.
        </h2>
        <div className="space-y-6 text-lg text-[var(--beautix-muted)] leading-relaxed font-light">
          <p>
            Salon powstał z prostej potrzeby — stworzenia miejsca, w którym każda
            klientka czuje się jak w domu. Miłej, swobodnej atmosfery szukamy
            w każdym detalu: od zapachu w gabinecie, przez muzykę, po sposób,
            w jaki witamy się z Tobą przy drzwiach.
          </p>
          <p>
            Nasze usługi w BEAUTIX zaczynają się na Twoich paznokciach, przechodzą
            do stóp i sięgają aż do skóry twarzy. Każda z tych dziedzin wymaga
            innej wiedzy — dlatego w zespole mamy specjalistki, które szkolą się
            na bieżąco i mogą zaproponować Ci naprawdę aktualne rozwiązania.
          </p>
          <p>
            Ocena <strong className="text-[var(--beautix-ink)]">4,9/5 z 117 opinii</strong>{" "}
            na Google, Facebooku i panoramafirm.pl — to wynik, z którego jesteśmy
            dumne, ale traktujemy go jako zobowiązanie, nie punkt docelowy.
          </p>
        </div>
      </section>

      <Reviews compact />
      <Team />
      <Gallery variant="marquee" />
      <CtaBanner />
    </>
  );
}
